import { MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds/components/text";
import { UsageProgress } from "@/pages/settings/usage-progress";
import {
  MDSPlanCard,
  MDSPlanCardBackground,
  MDSPlanCardBorder,
  MDSPlanCardSlot,
  MDSPlanId,
  MDSIconKind,
} from "@/mds";
import { BillingStrategyEnum } from "@/store/pages/SettingsSubscriptionManagementPageStore/types";
import { MDSBadge, MDSBadgeVariant } from "@/mds/components/badge";
import { MDSButton, MDSButtonVariant } from "@/mds/components/buttons";
import { MDSIcon } from "@/mds/components/icon";
import { getProFeatures } from "@/pages/settings/components/subscription-management/data";
import { ProactiveSubscriptionPlanCard } from "@/pages/settings/components/subscription-management/proactive";
import {
  CardsRow,
  PlanContainer,
  TitleAndBadgeContainer,
} from "@/pages/settings/components/subscription-management/subscription.styles";
import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";

interface Props {
  renewalDateText: string;
  onSubscribe: (billingStrategy: BillingStrategyEnum) => void;
  onSubscribeToProactive: () => void;
  isProactiveTrialEligible: boolean;
  proactivePriceText: string;
  isSubscribingToPro: boolean;
  isSubscribingToProactive: boolean;
  usageProgress?: ReactNode;
}

export const FreePlanManagement: React.FC<Props> = observer(
  ({
    renewalDateText,
    onSubscribe: handleSubscribe,
    onSubscribeToProactive: handleSubscribeToProactive,
    isProactiveTrialEligible,
    proactivePriceText,
    isSubscribingToPro,
    isSubscribingToProactive,
    usageProgress,
  }) => {
    return (
      <>
        <TitleAndBadgeContainer>
          <MDSText variant={MDSTextVariant.HeaderL} color={MDSTextColor.Contrast}>
            Free Plan
          </MDSText>
          <MDSBadge
            label="Upgrade"
            variant={MDSBadgeVariant.EmphasisBig}
            icon={MDSIconKind.StarSolid}
          />
        </TitleAndBadgeContainer>
        <PlanContainer>
          <MDSText>Monthly limits will reset on {renewalDateText}</MDSText>
          {usageProgress ?? <UsageProgress />}

          <CardsRow $columns>
            <MDSPlanCard
              plan={MDSPlanId.Paid}
              features={getProFeatures()}
              border={MDSPlanCardBorder.Default}
              background={MDSPlanCardBackground.Default}
            >
              <MDSText
                variant={MDSTextVariant.HeaderL}
                weight={MDSTextWeight.SemiBold}
                slot={MDSPlanCardSlot.Title}
              >
                Pro
              </MDSText>
              <div slot={MDSPlanCardSlot.Price}>$12/month</div>

              <MDSButton
                variant={MDSButtonVariant.Default}
                onClick={() => handleSubscribe(BillingStrategyEnum.MONTHLY)}
                isLoading={isSubscribingToPro}
                isDisabled={isSubscribingToPro || isSubscribingToProactive}
                slot={MDSPlanCardSlot.Footer}
              >
                <MDSIcon kind={MDSIconKind.StarSolid} /> Upgrade to Pro
              </MDSButton>
            </MDSPlanCard>

            <ProactiveSubscriptionPlanCard
              badgeLabel="Recommended"
              isTrialEligible={isProactiveTrialEligible}
              proactivePriceText={proactivePriceText}
              isLoading={isSubscribingToProactive}
              isDisabled={isSubscribingToPro || isSubscribingToProactive}
              onSubscribe={handleSubscribeToProactive}
            />
          </CardsRow>
        </PlanContainer>
      </>
    );
  }
);
